import { useJobillaOrgId } from '@/hooks'
import { useMediaImageGenerate } from '@jobilla/nexus-sdk'
import i18next from 'i18next'
import { useCallback } from 'react'
import { formatFileValidationError, logger, useAlert, validateImageFile } from '../../ui'
import { emitUnhandledError } from '../../ui'
import { useEditorDirectImageTools } from '../use-editor-direct-image-tools'
import { useNewEditorOrgAssets } from './-useNewEditorOrgAssets'

export type UseDirectImageToolsProps = {
	campaignId: string
}

/**
 * Hook that provides image upload and generation callbacks via atoms.
 * This hook sets up the callbacks in atoms so they can be accessed by widgets.
 */
export function useDirectImageTools({ campaignId }: UseDirectImageToolsProps) {
	const jobillaOrgId = useJobillaOrgId()
	const alert = useAlert()
	const { onUpload, onUploadAsync } = useNewEditorOrgAssets()

	const organisationId = jobillaOrgId

	const { mutate: imageGenerateMutate } = useMediaImageGenerate({
		mutationKey: ['generate-campaign-image'],
	})

	const getErrorMessage = (error: unknown): string => {
		if (error instanceof Error && error.message) { return error.message }
		if (typeof error === 'string') { return error }
		const maybeMessage = (error as { message?: unknown })?.message
		if (typeof maybeMessage === 'string' && maybeMessage) { return maybeMessage }
		return i18next.t('editor:errors.failedGenerate')
	}

	const handleImageUpload = useCallback((files: FileList) => {
		if (!files || files.length === 0) { return }

		const file = files[0]

		// Validate file before upload
		const validationError = validateImageFile(file)
		if (validationError) {
			alert.open(
				validationError.type === 'file-too-large'
					? i18next.t('editor:imageValidation.imageTooLargeTitle')
					: i18next.t('editor:imageValidation.invalidFileTypeTitle'),
				formatFileValidationError(validationError),
				'OK',
				() => {},
			)
			return
		}

		onUpload(files, 'ad-asset')
	}, [onUpload, alert])

	const handleQuickImageUpload = useCallback((
		file: File,
		onComplete: (uuid: string, url: string) => void,
		onError?: () => void,
	) => {
		if (!file) {
			onError?.()
			return
		}

		// Validate file before upload
		const validationError = validateImageFile(file)
		if (validationError) {
			alert.open(
				validationError.type === 'file-too-large'
					? i18next.t('editor:imageValidation.imageTooLargeTitle')
					: i18next.t('editor:imageValidation.invalidFileTypeTitle'),
				formatFileValidationError(validationError),
				'OK',
				() => {},
			)
			onError?.()
			return
		}

		onUploadAsync(file, 'ad-asset')
			.then((response) => {
				// id and url are required by the spec; an empty one means the upload
				// reported success without a usable asset, which is recoverable here
				// but must not pass silently.
				if (response.id.length > 0 && response.url.length > 0) {
					onComplete(response.id, response.url)
				} else {
					logger.error(new Error('Image upload returned an empty id or url'), {
						tags: { component: 'DirectImageTools', action: 'quick-upload' },
						data: { imageId: response.id, imageUrl: response.url },
					})
					// TODO: Pass error payload to onError once signature supports it
					onError?.()
				}
			})
			.catch((error) => {
				// Error already handled by emitUnhandledError in useNewEditorOrgAssets
				logger.error(error, {
					tags: { component: 'DirectImageTools', action: 'quick-upload' },
				})
				// TODO: Pass error payload to onError once signature supports it
				onError?.()
			})
	}, [onUploadAsync, alert])

	const handleQuickImageGenerate = useCallback((
		prompt: string,
		onComplete: (uuid: string) => void,
		onError?: (message: string) => void,
	) => {
		if (!organisationId || typeof organisationId !== 'string') { return }

		imageGenerateMutate({
			organisation: organisationId,
			data: {
				additionalInstructions: prompt,
				campaignId,
			},
		}, {
			onSuccess: (data) => {
				if (data?.id) {
					onComplete(data.id)
				}
			},
			onError: (error) => {
				const message = getErrorMessage(error)
				logger.error(error, {
					tags: { component: 'DirectImageTools', action: 'generate' },
				})
				emitUnhandledError({
					title: i18next.t('editor:errors.failedGenerate'),
					description: message,
					cause: error,
				})
				onError?.(message)
			},
		})
	}, [campaignId, organisationId, imageGenerateMutate])

	useEditorDirectImageTools({
		onImageUpload: handleImageUpload,
		onQuickImageUpload: handleQuickImageUpload,
		onQuickImageGenerate: handleQuickImageGenerate,
	})
}
